import { createRefreshScheduler } from '../../utils/refreshScheduler'
import { Controller } from '@hotwired/stimulus'
import {
  trans, REMOVE_MEDIA_ERROR_MESSAGE, UNDO_MEDIA_ERROR_MESSAGE, REMOVE_MEDIA_ERROR_TITLE, UNDO_MEDIA_ERROR_TITLE,
  ADD_MEDIA_ERROR_TITLE, MAX_FILES_ERROR_MESSAGE, ADD_MEDIA_PERMISSION_ERROR_MESSAGE, ADD_MEDIA_SIZE_ERROR_MESSAGE,
  ADD_MEDIA_TIMEOUT_ERROR_MESSAGE, ADD_MEDIA_GENERIC_ERROR_MESSAGE, ADD_MEDIA
} from '../../translator'
import 'lightgallery.js'
import 'lightgallery.js/dist/css/lightgallery.min.css'
import Modal from 'bootstrap/js/dist/modal'
import { initialiseFileValidator, validateFileSize } from '../../entrypoints/report/components/file_validation.js'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'

export default class extends Controller {
  static values = {
    recordId: String,
    recordType: String,
    fileCountMax: Number,
    fileCount: Number,
    checklistTaskId: String,
    imageMaxSize: Number,
    videoMaxSize: Number,
    audioMaxSize: Number,
    pdfMaxSize: Number,
    spreadsheetMaxSize: Number
  }

  static targets = [
    'recordMediaContainer',
    'createMedia',
    'lightgallery', 'imageLoading', 'imageLoadingSrc',
    'thumbnails', 'mediaLoading',
    'undoModal', 'removeModal',
    'validationModal', 'validationModalTitle', 'validationModalMessage',
    'mediaBadge', 'commentContainer', 'addMediaLabel',
    'dragAndDrop'
  ]

  undoModal = null
  removeModal = null
  validationModal = null
  mediaToRemove = null
  refreshUrl = null

  connect () {
    this.refresh = createRefreshScheduler(context => this.reloadPanel(context))
    this.initLightGallery()
    this.initDragAndDrop()
    initialiseFileValidator(this.imageMaxSizeValue, this.videoMaxSizeValue, this.audioMaxSizeValue, this.pdfMaxSizeValue, this.spreadsheetMaxSizeValue)

    this.handleFileChange = (event) => {
      this.uploadFiles(event.currentTarget.files)
    }
    this.createMediaTarget.addEventListener('change', this.handleFileChange)

    this.undoModal = new Modal(this.undoModalTarget)
    this.removeModal = new Modal(this.removeModalTarget)
    this.validationModal = new Modal(this.validationModalTarget)

    this.handleRefreshMediaPanel = (event) => this.refreshPanel(event.detail.url)
    document.addEventListener('RefreshMediaPanel', this.handleRefreshMediaPanel)
  }

  disconnect () {
    super.disconnect()

    this.createMediaTarget.removeEventListener('change', this.handleFileChange)
    document.removeEventListener('RefreshMediaPanel', this.handleRefreshMediaPanel)
    this.refresh.dispose()
  }

  refreshPanel (url) {
    this.refreshUrl = url
    this.refresh.request()
  }

  async reloadPanel ({ isCurrent }) {
    const response = await fetchWithHandlingLoginRedirect(this.refreshUrl, { reloadOnNetworkError: false })

    const data = await response.json()
    if (!isCurrent()) return

    this.recordMediaContainerTarget.innerHTML = data.mediaList
    if (data.hasMedia) {
      this.recordMediaContainerTarget.classList.remove('d-none')
    }
    this.fileCountValue = data.mediaCount
    this.updateMediaCounts()
    this.initLightGallery()

    if (this.hasUndoModalTarget) {
      this.undoModal = new Modal(this.undoModalTarget)
    }
    if (this.hasRemoveModalTarget) {
      this.removeModal = new Modal(this.removeModalTarget)
    }
  }

  initLightGallery () {
    this.lightgalleryTargets.forEach((lg) => {
      lg.addEventListener('onBeforeOpen', function (e) {
        $('#sidenav-main').addClass('d-none')
      }, false)

      lg.addEventListener('onBeforeClose', function (e) {
        $('#sidenav-main').removeClass('d-none')
      }, false)

      lightGallery(lg, {
        mode: 'lg-fade',
        thumbnail: true
      })
    })
  }

  initDragAndDrop () {
    const htmlElement = document.querySelector('html')

    htmlElement.addEventListener('dragover', (event) => {
      event.preventDefault()
      event.stopPropagation()
    })

    htmlElement.addEventListener('drop', (event) => {
      event.preventDefault()
      event.stopPropagation()
      this.hideDragAndDropOverlay()
    })

    if (this.recordTypeValue === 'CHECKLIST') {
      this.element.addEventListener('dragover', (event) => {
        event.preventDefault()
        event.stopPropagation()
      })

      this.element.addEventListener('dragenter', (event) => {
        event.preventDefault()
        event.stopPropagation()
        this.showDragAndDropOverlay()
      })

      this.element.addEventListener('dragleave', (event) => {
        event.preventDefault()
        event.stopPropagation()
        this.hideDragAndDropOverlay()
      })

      this.element.addEventListener('drop', (event) => {
        this.handleDropEvent(event)
      })
    } else {
      htmlElement.addEventListener('dragenter', (event) => {
        event.preventDefault()
        event.stopPropagation()
        this.showDragAndDropOverlay()
      })

      htmlElement.addEventListener('mouseenter', (event) => {
        event.preventDefault()
        event.stopPropagation()
        this.hideDragAndDropOverlay()
      })

      if (this.hasDragAndDropTarget) {
        this.dragAndDropTarget.addEventListener('drop', (event) => {
          this.handleDropEvent(event)
        })
      }
    }
  }

  showDragAndDropOverlay () {
    if (this.recordTypeValue === 'CHECKLIST') {
      this.element.classList.add('checklist-task-drag-and-drop-overlay')
    } else {
      if (this.hasDragAndDropTarget) {
        this.dragAndDropTarget.classList.add('controls-card-overlay')
      }
    }
  }

  hideDragAndDropOverlay () {
    if (this.recordTypeValue === 'CHECKLIST') {
      this.element.classList.remove('checklist-task-drag-and-drop-overlay')
    } else {
      if (this.hasDragAndDropTarget) {
        this.dragAndDropTarget.classList.remove('controls-card-overlay')
      }
    }
  }

  handleDropEvent (event) {
    event.preventDefault()
    event.stopPropagation()
    this.hideDragAndDropOverlay()
    this.uploadFiles(event.dataTransfer.files)
  }

  showThumbnail (file) {
    if (this.hasMediaBadgeTarget) {
      if (this.mediaBadgeTarget.classList.contains('d-none')) {
        this.mediaBadgeTarget.classList.remove('d-none')
      }
      if (this.hasCommentContainerTarget) {
        if (this.commentContainerTarget.classList.contains('d-none')) { // hasn't been opened
          this.commentContainerTarget.classList.remove('d-none')
        }
        if (!this.commentContainerTarget.classList.contains('show')) { // collapsed
          this.mediaBadgeTarget.click()
        }
      }
    }

    if (file.type.startsWith('image')) {
      const fileReader = new FileReader()
      fileReader.readAsDataURL(file)
      fileReader.onload = (event) => {
        this.imageLoadingSrcTarget.src = event.target.result
        this.imageLoadingTarget.classList.remove('d-none')
      }
    } else {
      this.mediaLoadingTarget.classList.remove('d-none')
    }
  }

  isFileValid (file) {
    const validationResponseObject = validateFileSize(file)
    if (!validationResponseObject.isValid) {
      this.showValidationModal(validationResponseObject.title, validationResponseObject.errorMessage)
      return
    }

    return true
  }

  uploadFiles (files) {
    if (this.fileCountValue + files.length > this.fileCountMaxValue) {
      this.showValidationModal(
        trans(ADD_MEDIA_ERROR_TITLE, {}, 'records'),
        trans(MAX_FILES_ERROR_MESSAGE, { '%max%': this.fileCountMaxValue }, 'records')
      )
      return
    }

    for (const file of [...files]) {
      if (this.isFileValid(file)) {
        this.showThumbnail(file)
        this.uploadFile(file)
      }
    }
  }

  uploadFile (file) {
    if (this.fileCountValue === 0) {
      this.recordMediaContainerTarget.classList.remove('d-none')
    }

    let url = `/internal/records/media?recordType=${this.recordTypeValue}`
    if (this.recordIdValue) {
      url += `&recordId=${this.recordIdValue}`
      if (this.checklistTaskIdValue) {
        url += `&checklistTaskId=${this.checklistTaskIdValue}`
      }
    }

    const data = new FormData()
    data.append('file', file)
    fetchWithHandlingLoginRedirect(url, { method: 'POST', body: data })
      .then(response => {
        if (!response.ok) {
          return Promise.reject(response)
        }

        this.fileCountValue++
        this.updateMediaCounts()
        return response.json()
      })
      .then((json) => {
        this.hideThumbnail()
        if (json.mediaType === 'image') {
          this.addImageThumbnail(json.media)
        } else {
          this.addMediaThumbnail(json.media)
        }

        return json.mediaId
      })
      .then((mediaId) => {
        document.dispatchEvent(new CustomEvent('MediaUploaded', {
          detail: { mediaId }
        }))
      })
      .catch((response) => {
        this.hideThumbnail()

        switch (response.status) {
          case 403: this.showValidationModal(trans(ADD_MEDIA_ERROR_TITLE, {}, 'records'), trans(ADD_MEDIA_PERMISSION_ERROR_MESSAGE, {}, 'records')); break
          case 413: this.showValidationModal(trans(ADD_MEDIA_ERROR_TITLE, {}, 'records'), trans(ADD_MEDIA_SIZE_ERROR_MESSAGE, {}, 'records')); break
          case 504: this.showValidationModal(trans(ADD_MEDIA_ERROR_TITLE, {}, 'records'), trans(ADD_MEDIA_TIMEOUT_ERROR_MESSAGE, {}, 'records')); break
          default: this.showValidationModal(trans(ADD_MEDIA_ERROR_TITLE, {}, 'records'), trans(ADD_MEDIA_GENERIC_ERROR_MESSAGE, {}, 'records')); break
        }
      })
  }

  hideThumbnail () {
    this.imageLoadingTarget.classList.add('d-none')
    this.mediaLoadingTarget.classList.add('d-none')
  }

  addImageThumbnail (media) {
    this.lightgalleryTarget.insertAdjacentHTML('beforeend', media)
    this.initLightGallery()
  }

  addMediaThumbnail (media) {
    this.thumbnailsTarget.insertAdjacentHTML('beforeend', media)
  }

  remove (event) {
    if (event.detail.isUndo) {
      this.undoModal.show()
      this.mediaToRemove = event.currentTarget
    } else {
      this.removeModal.show()
      this.mediaToRemove = event.currentTarget
    }
  }

  confirmUndo () {
    this.undoModal.hide()
    this.removeMedia(this.mediaToRemove.dataset.mediaId, true)
  }

  confirmRemove () {
    this.removeModal.hide()
    this.removeMedia(this.mediaToRemove.dataset.mediaId, false)
  }

  removeMedia (mediaId, isUndo) {
    fetchWithHandlingLoginRedirect(`/internal/media/${mediaId}?recordType=${this.recordTypeValue}` + (isUndo ? `&${new URLSearchParams({ undo: 'true' })}` : ''), { method: 'DELETE' })
      .then(response => {
        if (!response.ok) {
          return Promise.reject(response)
        }

        return response.json
      })
      .then((json) => {
        this.mediaToRemove.remove()
        this.fileCountValue--
        this.updateMediaCounts()

        return json.mediaId
      })
      .then((mediaId) => {
        document.dispatchEvent(new CustomEvent('MediaRemoved', {
          detail: { mediaId }
        }))
      })
      .catch((response) => {
        this.showValidationModal(
          trans(isUndo ? UNDO_MEDIA_ERROR_TITLE : REMOVE_MEDIA_ERROR_TITLE, {}, 'records'),
          trans(isUndo ? UNDO_MEDIA_ERROR_MESSAGE : REMOVE_MEDIA_ERROR_MESSAGE, {}, 'records')
        )
      })
  }

  showValidationModal (title, message) {
    this.validationModalTitleTarget.innerHTML = title
    this.validationModalMessageTarget.innerHTML = message
    this.validationModal.show()
  }

  updateMediaCounts () {
    this.updateMediaBadgeCount()
    this.updateAddMediaLabel()
  }

  updateMediaBadgeCount () {
    if (!this.hasMediaBadgeTarget) {
      return
    }

    if (this.fileCountValue > 0) {
      this.mediaBadgeTarget.classList.remove('d-none')
    } else {
      this.mediaBadgeTarget.classList.add('d-none')
    }

    this.mediaBadgeTarget.innerHTML = `<i class="fas fa-image mr-1"></i>${this.fileCountValue}`
  }

  updateAddMediaLabel () {
    if (!this.hasAddMediaLabelTarget) {
      return
    }

    this.addMediaLabelTarget.innerHTML = trans(ADD_MEDIA, {}, 'checklists') + (this.fileCountValue > 0 ? ` (${this.fileCountValue})` : '')
  }
}
